/**
 * @file Recalls export-selection modal and CSV download orchestration.
 */

import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { exportRecalls } from "./recallsApi.js";
import { openRecallsConfigIncompleteModal } from "./recallsConfigIncomplete.js";
import { RECALLS_MODAL_STACK_CLASSES } from "./recallsModalLayout.js";

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function getTodayDateString() {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 10);
}

function getCallableErrorMessage(error) {
    const validationErrors = error?.details?.errors || error?.customData?.errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        return validationErrors
            .slice(0, 4)
            .map((entry) => entry.message || entry.field || "Invalid row")
            .join(" ");
    }
    return error?.message || "The selected Recalls could not be exported.";
}

function setModalError(message) {
    const errorEl = document.querySelector("[data-recalls-export-error]");
    if (errorEl) {
        errorEl.textContent = message || "";
    }
}

function downloadCsv(csv, fileName) {
    const blob = new Blob([csv || ""], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "Email Recalls.csv";
    link.className = "hidden";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function getExportModalHtml(selectedCount) {
    const today = getTodayDateString();
    return `
        <div class="${RECALLS_MODAL_STACK_CLASSES}">
            <p class="text-sm text-gray-600">
                Export ${selectedCount} selected Recall${selectedCount === 1 ? "" : "s"}
                to the marketing CSV format.
            </p>
            <div data-recalls-export-email-date-control></div>
            <input type="hidden" value="${escapeHTML(today)}" data-recalls-export-email-date>
            <p class="text-sm text-red-700" data-recalls-export-error></p>
        </div>
    `;
}

function mountExportModalControls() {
    const root = document.querySelector("[data-recalls-export-email-date-control]");
    const mirrorInput = document.querySelector("[data-recalls-export-email-date]");
    if (!root || !mirrorInput) {return;}

    createDateControl({
        root,
        mirrorInput,
        name: "email_date",
        type: "date",
        value: mirrorInput.value || getTodayDateString(),
        label: "Email date",
    });
}

/**
 * Creates a Recalls export modal controller.
 * @param {object} [deps={}] Dependency bag.
 * @param {Function} [deps.downloadCsvFn] CSV download function.
 * @param {Function} [deps.exportRecallsFn] Export callable wrapper.
 * @param {Function} [deps.openModal] Modal opener.
 * @returns {object} Export modal controller.
 */
export function createRecallsExportModalController(deps = {}) {
    const downloadCsvFn = deps.downloadCsvFn || downloadCsv;
    const exportRecallsFn = deps.exportRecallsFn || exportRecalls;
    const openModal = deps.openModal || openConfirmationModal;

    return {
        /**
         * Opens the Recalls export modal for selected rows.
         * @param {object} params Modal params.
         * @param {Array<string>} params.recallIds Selected Recall ids.
         * @param {Function} [params.onSuccess] Successful export callback.
         * @returns {void}
         */
        openRecallsExportModal({ recallIds = [], onSuccess = null }) {
            const selectedIds = recallIds.filter(Boolean);
            if (selectedIds.length === 0) {return;}

            openModal(
                "Export Recalls",
                getExportModalHtml(selectedIds.length),
                async () => {
                    const input = document.querySelector(
                        "[data-recalls-export-email-date]",
                    );
                    const emailDate = input?.value || "";
                    if (!DATE_PATTERN.test(emailDate)) {
                        setModalError("Email date is required.");
                        return false;
                    }

                    try {
                        setModalError("");
                        const response = await exportRecallsFn({
                            recallIds: selectedIds,
                            email_date: emailDate,
                        });
                        downloadCsvFn(response.csv, response.fileName);
                        onSuccess?.(response);
                        return true;
                    } catch (error) {
                        if (openRecallsConfigIncompleteModal(error)) {
                            return false;
                        }
                        setModalError(getCallableErrorMessage(error));
                        return false;
                    }
                },
                false,
                "Export",
                null,
                "Exporting...",
                "Cancel",
                {
                    closeOnError: false,
                    keepOpenOnFalse: true,
                    maxWidth: "2xl",
                },
            );
            mountExportModalControls();
        },
    };
}

const configuredExportController = createRecallsExportModalController();

export const openRecallsExportModal =
    configuredExportController.openRecallsExportModal;
